import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { getAuthToken, getAuthUserId } from '@/utils/auth';
import {
  handleAuthExpiredRedirect,
  resolveFriendlyErrorMessage,
  shouldHandleWischoicerAuthExpired,
} from '@/utils/auth-expired';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  message?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, message, showType } =
        res as unknown as ResponseStructure;
      if (success === false) {
        // wischoicer 后端用 errorMessage，new-api 用 message
        const msg = errorMessage || message || '';
        const error: any = new Error(msg);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage: msg, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      // WIS-533：鉴权过期统一兜底，放在 skipErrorHandler guard 之前。
      // Umi request 运行时对所有请求错误都会调用 errorHandler（skipErrorHandler 是
      // 由下方 guard 实现的），因此这里一次性覆盖所有走 Umi request 的路径——
      // 含 skipErrorHandler:true 的执行服务、历史消息、用户资料/会员、
      // 视频复刻等。命中后清登录态 + 跳登录页，不再弹技术报错。
      // `/bff/token-platform/**` 使用独立 new-api PAT，由费用页本地重连，不清 Wischoicer 登录态。
      // 运行时仍会 reject(error)，调用方 catch 照常触发。
      if (shouldHandleWischoicerAuthExpired(error)) {
        handleAuthExpiredRedirect(error);
        return;
      }
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        // WIS-533：不再弹 `Response status:xxx`，给可理解文案（优先后端 message）
        message.error(resolveFriendlyErrorMessage(error));
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('网络异常，请检查网络后重试');
      } else {
        // 发送请求时出了点问题
        message.error('请求失败，请稍后重试');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const authorization = getAuthToken();
      if (!authorization) return config;
      const userId = getAuthUserId();

      return {
        ...config,
        headers: {
          Authorization: authorization,
          ...(userId ? { user_id: userId } : {}),
          ...(config.headers || {}),
        },
      };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        // WIS-533：2xx 但语义为鉴权过期的业务体（响应拦截器不被 skipErrorHandler 跳过，
        // 因此也能兜住 skipErrorHandler:true 的调用）→ 统一清登录态 + 跳登录页。
        // 必须传完整 response 才能识别请求 URL，隔离 new-api PAT 与 Wischoicer JWT 鉴权域。
        if (shouldHandleWischoicerAuthExpired(response)) {
          handleAuthExpiredRedirect(data);
          return response;
        }
        // 如果响应已包含 errorMessage 或 message（new-api 风格），
        // 说明是业务逻辑错误，交给调用方自行处理
        // 只有无明确错误信息时才弹全局提示
        if (!data?.errorMessage && !data?.message) {
          message.error('请求失败！');
        }
      }
      return response;
    },
  ],
};
